import { useState, useEffect } from 'react';
import type { Video } from '../../../types';
import type { BreakpointLabel, Breakpoints } from '../../utils/breakpoints';
import { breakpoints } from '../../utils/breakpoints';

const getInitialBreakpoint = (videos: Partial<Breakpoints<Video>>): BreakpointLabel => {
  const videoKeys = Object.keys(videos) as BreakpointLabel[];

  // check if there’s any breakpoint > "s" to make landscape the default
  const largerThanS = (['m', 'l', 'xl', 'xxl'] as BreakpointLabel[]).find((bp) =>
    videoKeys.includes(bp),
  );

  return largerThanS ?? 'xs';
};
export const useBreakpoint = (videos: Partial<Breakpoints<Video>>) => {
  const [breakpoint, setBreakpoint] = useState<BreakpointLabel>(getInitialBreakpoint(videos));
  const [nextAvailableBreakPoint, setNextAvailableBreakPoint] = useState<BreakpointLabel>('xs');

  useEffect(() => {
    const calcBreakpoint = (): BreakpointLabel => {
      const width = window.innerWidth;
      if (width >= breakpoints.xxl) return 'xxl';
      if (width >= breakpoints.xl) return 'xl';
      if (width >= breakpoints.l) return 'l';
      if (width >= breakpoints.m) return 'm';
      if (width >= breakpoints.s) return 's';
      return 'xs';
    };

    const calcAvailableBreakpoint = (currBreakPoint: BreakpointLabel) => {
      const videoKeys = Object.keys(videos)
        .sort((a, b) => breakpoints[a as BreakpointLabel] - breakpoints[b as BreakpointLabel])
        .map((k) => k as BreakpointLabel);

      // exact match
      if (videoKeys.includes(currBreakPoint)) return currBreakPoint;

      const allKeys = Object.keys(breakpoints) as BreakpointLabel[];
      const currentIndex = allKeys.indexOf(currBreakPoint);

      // find next bigger
      const getNextBiggerBreakPoint = (): BreakpointLabel | undefined => {
        for (let i = currentIndex + 1; i < allKeys.length; i++) {
          if (videoKeys.includes(allKeys[i])) return allKeys[i];
        }
        return undefined;
      };

      // find next smaller
      const getNextSmallerBreakPoint = (): BreakpointLabel | undefined => {
        for (let i = currentIndex - 1; i >= 0; i--) {
          if (videoKeys.includes(allKeys[i])) return allKeys[i];
        }
        return undefined;
      };

      const nextBiggerBreakPoint = getNextBiggerBreakPoint();
      const nextSmallerBreakPoint = getNextSmallerBreakPoint();

      // preference logic
      if (currBreakPoint === 'xs' || currBreakPoint === 's') {
        // prefer smaller first
        if (nextSmallerBreakPoint) return nextSmallerBreakPoint;
        if (nextBiggerBreakPoint) return nextBiggerBreakPoint;
      } else {
        // m or larger → prefer bigger first
        if (nextBiggerBreakPoint) return nextBiggerBreakPoint;
        if (nextSmallerBreakPoint) return nextSmallerBreakPoint;
      }

      return 'xs'; // fallback
    };

    const handleResize = () => {
      const currentBreakPoint = calcBreakpoint();
      setBreakpoint(currentBreakPoint);
      setNextAvailableBreakPoint(calcAvailableBreakpoint(currentBreakPoint));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [videos]);

  return { breakpoint, nextAvailableBreakPoint };
};
